// QuickStudyIncidentsView — admin revisa mensagens marcadas como `refused`
// pela camada de output-validation do QuickStudy (anti-injection /
// jailbreak / leak de prompt / off-topic).
// [[project-quickstudy]] [[project-prompt-injection-defense]]

import { useEffect, useState } from "preact/hooks";
import {
  AlertTriangle,
  Calendar,
  ChevronDown,
  ChevronRight,
  Loader2,
  ShieldAlert,
  User
} from "lucide-react";
import { api } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";

interface IncidentRow {
  messageId: string;
  conversationId: string;
  conversationTitle: string;
  personaLabel: string | null;
  assistantContent: string;
  model: string | null;
  createdAt: string;
  user: { id: string; name: string | null; email: string | null };
}

export function QuickStudyIncidentsView() {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState<IncidentRow[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  async function load() {
    setPhase("loading");
    setErrMsg(null);
    try {
      const r = await api.getQuickStudyIncidents({ days, limit: 200 });
      setRows(r.incidents);
      setPhase("ready");
    } catch (e) {
      setErrMsg(friendlyError(e));
      setPhase("error");
    }
  }

  useEffect(() => {
    void load();
  }, [days]);

  function toggleExpanded(id: string) {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  }

  return (
    <div class="space-y-4">
      <div class="rounded-md border border-amber-500/40 bg-amber-500/5 p-4">
        <div class="flex items-start gap-3">
          <ShieldAlert className="mt-0.5 size-5 shrink-0 text-amber-600" />
          <div class="space-y-1 text-sm">
            <h3 class="font-bold">Incidentes do Tira-dúvida</h3>
            <p class="text-muted-foreground">
              Mensagens em que a camada de validação detectou padrão suspeito —
              vazamento de prompt, tentativa de jailbreak, resposta off-topic
              que escapou do topic-lock, ou padrão de injection bem-sucedida.
              A resposta foi entregue ao aluno marcada com aviso, e fica aqui
              pra auditoria.
            </p>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-between gap-3 rounded-md border border-border bg-card p-3">
        <label class="flex items-center gap-2 text-xs font-medium">
          <Calendar className="size-3.5 text-muted-foreground" />
          Janela:
          <select
            value={days}
            title="Janela temporal"
            onChange={(e) =>
              setDays(parseInt((e.target as HTMLSelectElement).value, 10))
            }
            class="rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:border-violet-500"
          >
            <option value={1}>1 dia</option>
            <option value={7}>7 dias</option>
            <option value={30}>30 dias</option>
            <option value={90}>90 dias</option>
            <option value={365}>1 ano</option>
          </select>
        </label>
        <button
          type="button"
          onClick={() => void load()}
          disabled={phase === "loading"}
          class="rounded-md border border-border bg-card px-3 py-1 text-xs font-semibold hover:bg-muted disabled:opacity-50"
        >
          {phase === "loading" ? "Carregando…" : "Atualizar"}
        </button>
      </div>

      {phase === "loading" && (
        <div class="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Carregando…
        </div>
      )}

      {phase === "error" && (
        <div class="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {errMsg ?? "Falha ao carregar."}
        </div>
      )}

      {phase === "ready" && rows.length === 0 && (
        <div class="rounded-md border border-dashed border-border bg-card/40 px-6 py-10 text-center">
          <ShieldAlert className="mx-auto size-8 text-muted-foreground/50" />
          <h4 class="mt-3 text-sm font-semibold">Nenhum incidente na janela</h4>
          <p class="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
            Ou a validação não detectou padrão suspeito, ou o tira-dúvida
            ainda não foi usado o suficiente nesse período.
          </p>
        </div>
      )}

      {phase === "ready" && rows.length > 0 && (
        <div class="space-y-2">
          <div class="text-xs text-muted-foreground">
            {rows.length} {rows.length === 1 ? "incidente" : "incidentes"} nos últimos{" "}
            {days} {days === 1 ? "dia" : "dias"}.
          </div>
          {rows.map((r) => {
            const isOpen = expanded.has(r.messageId);
            return (
              <article
                key={r.messageId}
                class="rounded-md border border-border bg-card"
              >
                <button
                  type="button"
                  onClick={() => toggleExpanded(r.messageId)}
                  class="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-muted/50"
                >
                  <div class="flex min-w-0 flex-1 items-center gap-2">
                    {isOpen ? (
                      <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
                    )}
                    <AlertTriangle className="size-3.5 shrink-0 text-amber-600" />
                    <span class="truncate text-sm font-semibold">
                      {r.conversationTitle}
                    </span>
                  </div>
                  <div class="flex shrink-0 items-center gap-3 text-[11px] text-muted-foreground">
                    <span class="inline-flex items-center gap-1">
                      <User className="size-3" />
                      {r.user.name || r.user.email || r.user.id.slice(0, 8)}
                    </span>
                    <time>{formatDate(r.createdAt)}</time>
                  </div>
                </button>

                {isOpen && (
                  <div class="space-y-2 border-t border-border bg-background/30 px-3 py-3 text-xs">
                    <div class="grid grid-cols-2 gap-3">
                      <div>
                        <div class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                          Conversa
                        </div>
                        <div class="font-mono text-[10px]">
                          {r.conversationId}
                        </div>
                      </div>
                      <div>
                        <div class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                          Persona / modelo
                        </div>
                        <div>
                          {r.personaLabel ?? "—"}
                          {r.model && (
                            <span class="ml-1 text-muted-foreground">
                              · {r.model}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      <div class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        Conteúdo da resposta marcada
                      </div>
                      <pre class="mt-1 max-h-80 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-card p-2 text-[11px]">
                        {r.assistantContent}
                      </pre>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return iso;
  }
}
